import type { ChannelScope, LegacyList } from '@mypoz/shared';

import { legacyApi } from '@/lib/legacy-api.js';
import { OrdersRequestError } from './api.js';

/**
 * MYP-53 Slice 2 — reference-data endpoints for the create/edit-order flow.
 *
 * Endpoint names here are the registry's actual slugs (`channels`, `country`, `province`, `city`,
 * `order_services`, `package_status_for_order_list`), not the names the Jira card used in passing
 * (`channel_list`, `country_list`, ...) — the card's own shorthand does not match what
 * `endpoints.ts` registers, and calling the wrong slug fails outright rather than degrading.
 */

/**
 * `channels` — every shipping channel the vendor has, across every warehouse. Since MYP-87 this is
 * only a name dictionary (for orders whose channel the scoped list does not include); the picker
 * offers `neworder_getChannel`'s list instead.
 */
export async function fetchChannelRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('channels');
  if (!result.ok) throw new OrdersRequestError('channels', result.code, result.message);
  return result.data.list;
}

/**
 * `neworder_getChannel` — the channels a new order from these packages can use (MYP-87).
 *
 * All three parameters are required: leave any out and the vendor answers `code 2, 非法途径访问`,
 * which is what an earlier probe (sending only some of them) took for "unusable". `inordernoArr`
 * is the package ids, comma-separated, as the PDF documents it.
 */
export async function fetchOrderChannelRows(scope: ChannelScope): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('neworder_getChannel', {
    inordernoArr: scope.packageIds.join(','),
    warehouseid: scope.warehouseId,
    goodstype: scope.goodsType,
  });
  if (!result.ok) throw new OrdersRequestError('neworder_getChannel', result.code, result.message);
  return result.data.list;
}

/**
 * `order_services` — warehouse add-on services, keyed by warehouse id. The map is returned as it
 * came (MYP-119), so `normaliseOrderServices` knows which warehouse each group is listed under —
 * the same way `fetchPackageServices` hands over `packages_services`. An empty `[]` (how PHP
 * encodes an empty map) arrives as `list` and means no services.
 */
export async function fetchOrderServiceRows(): Promise<
  readonly unknown[] | Readonly<Record<string, unknown>>
> {
  const result = await legacyApi.call<LegacyList<unknown>>('order_services');
  if (!result.ok) throw new OrdersRequestError('order_services', result.code, result.message);
  return result.data.keyed ?? result.data.list;
}

/**
 * `package_status_for_order_list` — the vendor's own answer to "which packages can go on a new
 * order", as distinct codes (live: NEW, ARRIVE, IN). Deliberately not `package_list`'s own
 * `validStatus: 1` parameter, which the MYP-90 investigation found answers a different, narrower
 * question than this endpoint does.
 */
export async function fetchEligiblePackageStatusRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('package_status_for_order_list');
  if (!result.ok) {
    throw new OrdersRequestError('package_status_for_order_list', result.code, result.message);
  }
  return result.data.list;
}

/** `country` — every top-level destination. No request parameters. */
export async function fetchCountryRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('country');
  if (!result.ok) throw new OrdersRequestError('country', result.code, result.message);
  return result.data.list;
}

/**
 * `province` — every state of every country, in one request (MYP-88). Asked with no `countryid`,
 * the endpoint returns all 70 rows, each carrying its own `countryid`, which is what shows that 8
 * of the 14 countries have no states at all. (The filter parameter is `countryid`; passing
 * `country` is silently ignored and also returns everything.)
 */
export async function fetchAllProvinceRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('province');
  if (!result.ok) throw new OrdersRequestError('province', result.code, result.message);
  return result.data.list;
}

/**
 * `city` — the cities under one province. Filters correctly on `provinceid` despite the ticket's
 * own claim that every parameter combination returns the same full list — verified live, see
 * `order-catalog-wire.ts`'s file header for the corrected finding.
 */
export async function fetchCityRows(provinceCode: string): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('city', { provinceid: provinceCode });
  if (!result.ok) throw new OrdersRequestError('city', result.code, result.message);
  return result.data.list;
}
